import { VehicleConfig } from '@_models/vehicleInfo/vehicleConfig/vehicle-config.model';
import { Injectable } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { GlobalConstant } from '@shared/global.constant';
import { BatteryVersion } from '@shared/models/business/battery-version.model';
import { ModelEnergy } from '@shared/models/business/model-energy.model';
import { Utils } from '@shared/utils/utils';
import { ToastrService } from 'ngx-toastr';
import { Comparison } from '../../_models/comparison.model';
import { EnergySubtype } from '../../_models/vehicleCost/impactCo2/energy-subtype.model';
import { TcoBackendService } from '../api/tco-backend.service';
import { AlertSignalService } from '../logical/alert-signal.service';
import { ComparatorService } from '../logical/comparator.service';
import { GlobalInformationForm } from './global-information-form.service';
import { VehicleCostFormService } from './vehicle-cost-form.service';
import { VehicleInformationFormService } from './vehicle-information-form.service';

@Injectable({
	providedIn: 'root',
})
export class TcoFormService {
	public utils: Utils;
	public isBlock: boolean = true;

	constructor(
		public toastr: ToastrService,
		private router: Router,
		private translate: TranslateService,
		private dataService: TcoBackendService,
		private compService: ComparatorService,
		protected vehicleInfoFormService: VehicleInformationFormService,
		protected vehicleCostFormService: VehicleCostFormService,
		protected globalInfoFormService: GlobalInformationForm,
		protected alertsSignalsService: AlertSignalService,
	) {
		this.utils = new Utils(toastr, translate);
	}

	/**
	 * Get Country Emission Factor after loading a comparison
	 * @private
	 */
	public initTcoForms(comparison: Comparison) {
		if (
			comparison.globalInfo.comparisonType.id === GlobalConstant.COMPTYPE_ED ||
			comparison.globalInfo.comparisonType.id === GlobalConstant.COMPTYPE_EDG
		) {
			this.setEnergySubtypeForDiesel(comparison.vehicleInfo.vehicleConfig);
		}
		this.compService.setComparisonObs(comparison);
		this.initEachForms(comparison);
		this.initAlertSignals(comparison);
	}

	private setEnergySubtypeForDiesel(vehicleConfig: VehicleConfig) {
		if (!vehicleConfig.diesel.energySubtype) {
			vehicleConfig.diesel.energySubtype = new EnergySubtype();
			vehicleConfig.diesel.energySubtype.id = 1;
			vehicleConfig.diesel.energySubtype.name = GlobalConstant.BX;
			vehicleConfig.diesel.energySubtype.isDefault = true;
		}
	}

	private initEachForms(comparison: Comparison) {
		const comparisonTypeId = comparison.globalInfo.comparisonType.id;
		this.initVehicleConfigForm(comparison);
		this.globalInfoFormService.initGlobalForm(comparison.globalInfo);
		this.vehicleInfoFormService.initVehicleUsageForm(comparison.vehicleInfo.vehicleUsage);
		this.vehicleInfoFormService.initEnergyUsageForm(comparison.vehicleInfo.energyUsage, comparisonTypeId);
		this.vehicleCostFormService.initFinancialForm(comparison.vehicleCost.financials, comparisonTypeId, comparison.globalInfo.isDetail);
		this.vehicleCostFormService.initEnergyCostForm(comparison.vehicleCost.energyCost, comparisonTypeId);
		if (comparison.vehicleInfo.vehicleConfig.diesel) {
			this.vehicleCostFormService.initImpactCo2Form(
				comparison.vehicleCost.impactCo2,
				comparisonTypeId,
				comparison.vehicleInfo.vehicleConfig.diesel.energySubtype?.name,
			);
		} else {
			this.vehicleCostFormService.initImpactCo2Form(comparison.vehicleCost.impactCo2, comparisonTypeId);
		}
		this.vehicleCostFormService.initIncentivesForm(comparison.vehicleCost.energyIncentivesTaxe, comparisonTypeId);
		this.vehicleCostFormService.initTransportCostForm(comparison.vehicleCost.transportCost, comparisonTypeId);
	}

	private initAlertSignals(comparison: Comparison) {
		this.alertsSignalsService.clearAlertSignal();
		this.alertsSignalsService.resetAllAlerts();
		this.populateAlertsBasedOnDetailStatus(comparison);
		this.getAlertsForComparison(comparison);
	}

	private populateAlertsBasedOnDetailStatus(comparison: Comparison) {
		if (comparison.globalInfo.isDetail) {
			this.alertsSignalsService.populeAlertSignalsListForDetailed();
		} else {
			this.alertsSignalsService.populeAlertSignalsListForMonthly();
		}
	}

	private getAlertsForComparison(comparison: Comparison) {
		const controls = this.getFinancialControls(comparison.globalInfo.isDetail);
		if (!controls) {
			return;
		}
		this.updateTollApplicationPeriodControls(controls, comparison.vehicleCost.energyIncentivesTaxe);
		const selectedComparisons = this.compService.initComparisonsSelected(comparison);
		this.alertsSignalsService.getAlertSignals(selectedComparisons, false, true, controls);
	}

	private getFinancialControls(isDetail: boolean) {
		return isDetail
			? this.vehicleCostFormService.detailsFinancialForm?.controls
			: this.vehicleCostFormService.monthlyFeesForm?.controls;
	}

	private updateTollApplicationPeriodControls(controls: any, energyIncentivesTaxe: any) {
		for (const energyType in controls) {
			const group = controls[energyType];
			if (!(group instanceof FormGroup)) {
				continue;
			}
			const tollPeriod = this.extractTollApplicationPeriod(energyIncentivesTaxe, energyType);
			this.addOrUpdateControl(group, 'tollApplicationPeriod', tollPeriod);
		}
	}

	private extractTollApplicationPeriod(energyIncentivesTaxe: any, energyType: string): number | null {
		const incentives = energyIncentivesTaxe?.[energyType];
		if (!incentives || typeof incentives !== 'object') {
			return null;
		}
		return 'tollApplicationPeriod' in incentives ? incentives.tollApplicationPeriod : null;
	}

	private addOrUpdateControl(group: FormGroup, controlName: string, value: any): void {
		if (!group.contains(controlName)) {
			group.addControl(controlName, new FormControl(value));
		} else {
			group.get(controlName)!.setValue(value);
		}
	}

	/**
	 * Get  Model and Battery and update Electric Vehicle-config Form
	 */
	private initVehicleConfigForm(comparison: Comparison): void {
		this.vehicleInfoFormService.initVehicleConfigForm(comparison.vehicleInfo.vehicleConfig, comparison.globalInfo.comparisonType.id);
		this.findElectricModel(comparison.vehicleInfo.vehicleConfig, comparison.globalInfo.comparisonType.id);
		this.findElecBattery(comparison.vehicleInfo.vehicleConfig);
	}

	private findElecBattery(vehicleConfig: VehicleConfig): void {
		if (vehicleConfig.electric.battery?.code) {
			this.dataService.getBatteryByCode(vehicleConfig.electric.battery.code).subscribe({
				next: (result) => {
					vehicleConfig.electric.battery = result;
					this.vehicleInfoFormService.vehicleConfigForm.patchValue({ electric: { ['battery']: result } });
				},
			});
		} else {
		}
		this.vehicleInfoFormService.vehicleConfigForm.patchValue({ electric: { ['battery']: new BatteryVersion() } });
	}

	private findElectricModel(vehicleConfig: VehicleConfig, comparisonType: number): void {
		if (vehicleConfig.electric.model?.code) {
			this.dataService.getModelByCode(vehicleConfig.electric.model.code, this.getNavigatorLang()).subscribe((model: ModelEnergy) => {
				vehicleConfig.electric.model = model;
				this.vehicleInfoFormService.vehicleConfigForm.patchValue({ electric: { ['model']: vehicleConfig.electric.model } });
			});
		} else {
			this.vehicleInfoFormService.initVehicleConfigForm(vehicleConfig, comparisonType);
		}
	}

	/**  Get the language from the navigator */
	private getNavigatorLang(): string {
		return navigator.language.slice(0, 2).toLowerCase();
	}

	public canDeactivate(): void {
		const formEntries = [
			{ name: 'step.vehicleUsage', form: this.vehicleInfoFormService.vehicleUsageForm },
			{ name: 'step.vehicleConfig', form: this.vehicleInfoFormService.vehicleConfigForm },
			{ name: 'step.energyUsage', form: this.vehicleInfoFormService.energyUsageForm },
			{ name: 'step.energyCost', form: this.vehicleCostFormService.energyCostForm },
			{ name: 'step.incentivesTax', form: this.vehicleCostFormService.incentivesForm },
			{ name: 'step.impactCo2', form: this.vehicleCostFormService.impactCo2Form },
			{ name: 'step.costOfTransport', form: this.vehicleCostFormService.transportCostForm },
			{
				name: 'step.financials',
				form: this.vehicleCostFormService.detailsFinancialForm
					? this.vehicleCostFormService.detailsFinancialForm
					: this.vehicleCostFormService.monthlyFeesForm,
			},
		];
		const invalidForms = formEntries.filter((entry) => entry.form && entry.form.invalid).map((entry) => entry.name);
		if (invalidForms.length > 0) {
			this.preventToResultPage();

			this.translate.get(invalidForms).subscribe((translations) => {
				const messageToDisplay = '• ' + invalidForms.map((name) => translations[name]).join('<br> • ');
				this.utils.displayErrorMessage(messageToDisplay, 'button.fixErrors', true);
			});
			invalidForms.forEach((name) => {
				const form = formEntries.find((entry) => entry.name === name)?.form;
				form?.markAllAsTouched();
			});
			this.isBlock = true;
			return;
		}
		this.isBlock = false;
	}

	private preventToResultPage(): void {
		const currentRoute = this.router.routerState;
		this.router.navigateByUrl(currentRoute.snapshot.url, {
			skipLocationChange: true,
		});
	}
}
